import { jsonInit, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  normalizeSavedReportConfig,
  normalizeScheduledReportConfig,
  savedReportConfigSchema,
  scheduledReportConfigSchema,
  toReportShareUserOption,
  type ReportSchedulePayload,
  type SaveReportConfigPayload,
} from './model';

interface SavedReportsClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
  agencyId?: string | undefined;
}

const rawCollectionSchema = z.union([
  z.array(z.unknown()),
  z.looseObject({ content: z.array(z.unknown()) }).transform(({ content }) => content),
]);
const reportConfigListSchema = rawCollectionSchema.transform((items) =>
  parseValidCollectionItems(items, savedReportConfigSchema),
);
const scheduledReportListSchema = rawCollectionSchema.transform((items) =>
  parseValidCollectionItems(items, scheduledReportConfigSchema),
);

function parseValidCollectionItems<T>(items: unknown[], schema: z.ZodType<T>) {
  return items.flatMap((item) => {
    const result = schema.safeParse(item);
    return result.success ? [result.data] : [];
  });
}

export function createSavedReportsClient({
  config,
  accessToken,
  fetcher,
  agencyId,
}: SavedReportsClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);

  const base = agencyId ? `/api/agency/${encodeURIComponent(agencyId)}` : '/api';
  const reportsPath = `${base}/${agencyId ? 'report-configs' : 'report-config'}`;
  const schedulesPath = `${base}/scheduled-reports`;

  return {
    async listOwned() {
      const response = await api.request({
        path: reportsPath,
        schema: reportConfigListSchema,
      });
      return response.map((report) => normalizeSavedReportConfig(report));
    },
    async listShared() {
      const response = await api.request({
        path: `${reportsPath}/shared`,
        schema: reportConfigListSchema,
      });
      return response.map((report) => normalizeSavedReportConfig(report));
    },
    async listScheduled() {
      const response = await api.request({
        path: schedulesPath,
        schema: scheduledReportListSchema,
      });
      return response.map(normalizeScheduledReportConfig);
    },
    async create(payload: SaveReportConfigPayload) {
      const response = await api.request({
        path: reportsPath,
        schema: savedReportConfigSchema,
        init: jsonInit('POST', toSaveReportRequestPayload(payload)),
      });
      return normalizeSavedReportConfig(response, payload);
    },
    async update(id: string, payload: SaveReportConfigPayload) {
      const response = await api.request({
        path: `${reportsPath}/${encodeURIComponent(id)}`,
        schema: savedReportConfigSchema,
        init: jsonInit('PUT', toSaveReportRequestPayload(payload)),
      });
      return normalizeSavedReportConfig(response, payload);
    },
    delete(id: string) {
      return api.request({
        path: `${reportsPath}/${encodeURIComponent(id)}`,
        schema: z.unknown(),
        init: { method: 'DELETE' },
      });
    },
    async createSchedule(reportId: string, payload: ReportSchedulePayload) {
      await api.request({
        path: `${schedulesPath}/${encodeURIComponent(reportId)}`,
        schema: z.unknown(),
        init: jsonInit('POST', payload),
      });
    },
    async updateSchedule(scheduleId: string, payload: ReportSchedulePayload) {
      await api.request({
        path: `${schedulesPath}/${encodeURIComponent(scheduleId)}`,
        schema: z.unknown(),
        init: jsonInit('PUT', payload),
      });
    },
    deleteSchedule(scheduleId: string) {
      return api.request({
        path: `${schedulesPath}/${encodeURIComponent(scheduleId)}`,
        schema: z.unknown(),
        init: { method: 'DELETE' },
      });
    },
    async listShareUsers() {
      const users = await api.request({
        path: `/api/users/list-all?${toSearchParams({
          page: 0,
          size: 1000,
          enabled: true,
        })}`,
        schema: z.array(
          z.looseObject({
            id: z.union([z.string(), z.number()]).transform(String),
            name: z.string().nullish(),
            email: z.string().nullish(),
            username: z.string().nullish(),
          }),
        ),
      });
      return users.map((user) =>
        toReportShareUserOption({
          id: user.id,
          name: user.name ?? null,
          email: user.email ?? null,
          username: user.username ?? null,
        }),
      );
    },
  };
}

function toSaveReportRequestPayload({
  chartReport,
  settings,
  ...payload
}: SaveReportConfigPayload) {
  return {
    ...payload,
    chartReports: chartReport.map((report) => JSON.stringify(report)),
    settings: JSON.stringify(settings),
  };
}
